if (!customElements.get('hero-carousel-v2')) {
  const DESKTOP_QUERY = '(min-width: 1024px)';

  class HeroCarouselV2 extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-hero-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.videos = this.slides.map((slide) => slide.querySelector('[data-hero-video]'));
      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);

      this.editorPaused = false;
      this.pausedByPageState = false;

      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
      this.handlePageActivity = this.handlePageActivity.bind(this);

      if (this.hasAttribute('data-carousel') && this.slides.length > 1) {
        this.initCarousel();
      }

      this.activateSlide(this.embla ? this.embla.selectedScrollSnap() : 0);

      this.desktopQuery.addEventListener('change', this.handleBreakpointChange);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      document.addEventListener('visibilitychange', this.handlePageActivity);
      window.addEventListener('blur', this.handlePageActivity);
      window.addEventListener('focus', this.handlePageActivity);

      this.handlePageActivity();
    }

    disconnectedCallback() {
      this.desktopQuery.removeEventListener('change', this.handleBreakpointChange);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      document.removeEventListener('visibilitychange', this.handlePageActivity);
      window.removeEventListener('blur', this.handlePageActivity);
      window.removeEventListener('focus', this.handlePageActivity);

      this.editorPaused = false;
      this.pausedByPageState = false;

      if (this.embla) {
        this.embla.destroy();
        this.embla = null;
      }
      this.autoplayPlugin = null;
    }

    /* ------------------------------------------------------------------ */
    /* Carousel                                                            */
    /* ------------------------------------------------------------------ */

    initCarousel() {
      if (!this.viewport || typeof window.EmblaCarousel !== 'function') return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const autoplay = this.hasAttribute('data-autoplay') && !reducedMotion;
      const delay = parseInt(this.getAttribute('data-autoplay-interval'), 10) || 6000;

      const plugins = [];
      if (autoplay && typeof window.EmblaCarouselAutoplay === 'function') {
        this.autoplayPlugin = window.EmblaCarouselAutoplay({
          delay: delay,
          stopOnInteraction: false,
          stopOnMouseEnter: true,
        });
        plugins.push(this.autoplayPlugin);
      }

      this.embla = window.EmblaCarousel(
        this.viewport,
        {
          container: '[data-hero-track]',
          slides: '[data-hero-slide]',
          loop: this.hasAttribute('data-loop'),
          align: 'start',
          duration: 28,
        },
        plugins
      );

      this.setupDots();
      this.setupArrows();

      this.embla.on('select', () => {
        this.updateDots();
        this.activateSlide(this.embla.selectedScrollSnap());
      });
    }

    setupDots() {
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      if (!this.dots.length) return;

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.embla.scrollTo(index));
      });

      this.updateDots();
    }

    updateDots() {
      if (!this.dots || !this.dots.length) return;

      const selected = this.embla.selectedScrollSnap();
      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', index === selected ? 'true' : 'false');
      });
    }

    setupArrows() {
      const prevBtn = this.querySelector('[data-hero-prev]');
      const nextBtn = this.querySelector('[data-hero-next]');
      if (!prevBtn || !nextBtn) return;

      prevBtn.addEventListener('click', () => this.embla.scrollPrev());
      nextBtn.addEventListener('click', () => this.embla.scrollNext());

      const toggle = () => {
        prevBtn.disabled = !this.embla.canScrollPrev();
        nextBtn.disabled = !this.embla.canScrollNext();
      };

      this.embla.on('select', toggle).on('init', toggle).on('reInit', toggle);
      toggle();
    }

    activateSlide(index) {
      const last = this.videos.length - 1;
      const neighbours = [index, index - 1, index + 1].map((i) => {
        if (i < 0) return last;
        if (i > last) return 0;
        return i;
      });

      neighbours.forEach((i) => this.loadVideo(i));

      this.videos.forEach((video, i) => {
        if (!video) return;
        if (i === index) {
          this.playVideo(video);
        } else {
          video.pause();
        }
      });
    }

    loadVideo(index) {
      const video = this.videos[index];
      if (!video) return;

      const isDesktop = this.desktopQuery.matches;
      const desktopSrc = video.dataset.desktopSrc;
      const mobileSrc = video.dataset.mobileSrc;
      const src = (isDesktop ? desktopSrc : mobileSrc) || desktopSrc || mobileSrc;

      if (!src || video.dataset.loadedSrc === src) return;

      video.dataset.loadedSrc = src;
      video.classList.remove('is-ready');
      video.src = src;
      video.load();

      const markReady = () => video.classList.add('is-ready');
      video.addEventListener('loadeddata', markReady, { once: true });
    }

    playVideo(video) {
      if (!video.dataset.loadedSrc) return;

      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          document.addEventListener('touchstart', () => video.play().catch(() => { }), { once: true });
        });
      }
    }

    handleBreakpointChange() {
      this.videos.forEach((video, index) => {
        if (video && video.dataset.loadedSrc) this.loadVideo(index);
      });
      this.activateSlide(this.embla ? this.embla.selectedScrollSnap() : 0);
    }

    isRendering() {
      if (document.hidden) return false;

      if (window.Shopify && window.Shopify.designMode) return true;
      return document.hasFocus();
    }

    handlePageActivity() {
      const plugin = this.autoplayPlugin;
      if (!plugin) return;

      if (!this.isRendering()) {
        if (!plugin.isPlaying()) return;
        this.pausedByPageState = true;
        plugin.stop();
        return;
      }

      if (!this.pausedByPageState) return;
      this.pausedByPageState = false;
      this.resumeAutoplay();
    }

    resumeAutoplay() {
      const plugin = this.autoplayPlugin;
      if (!plugin || plugin.isPlaying()) return;
      if (this.editorPaused || !this.isRendering()) return;

      if (this.viewport && this.viewport.matches(':hover')) return;

      plugin.play();
    }

    handleBlockSelect(event) {
      if (!this.contains(event.target)) return;

      const index = this.slides.indexOf(event.target);
      if (index === -1) return;

      this.editorPaused = true;
      if (this.autoplayPlugin) this.autoplayPlugin.stop();
      if (this.embla) this.embla.scrollTo(index);
    }

    handleBlockDeselect(event) {
      if (!this.contains(event.target)) return;

      this.editorPaused = false;
      this.resumeAutoplay();
    }
  }

  customElements.define('hero-carousel-v2', HeroCarouselV2);
}
